import type { AuditLog } from '@/api/types/audit';
import { contactRestoreApi } from '@/api/queries/workspace';
import { Avatar, Button, Icon, IconButton, colorFromKey, initialsOf } from '@/components/ui';

import { actionMeta, formatHourMinute } from './auditUtils';

interface AuditDetailPanelProps {
  event: AuditLog;
  onClose: () => void;
  onOpenContact?: (ref: string) => void;
  /** Narrows the audit list to this contact (sets the quick-search to its id). */
  onFilterContact?: (personId: string) => void;
}

export function AuditDetailPanel({
  event,
  onClose,
  onOpenContact,
  onFilterContact,
}: AuditDetailPanelProps) {
  const meta = actionMeta(event.change?.action);
  const contactRef = event.docRef?.email || event.docRef?.personId || '';
  const actorId = event.actor?.id || 'system';
  const actorInitials = initialsOf(actorId.split('@')[0]);
  const restore = contactRestoreApi.useRestore();
  const restoreFromVersion = event.change?.versionFrom ?? 0;
  const personId = event.docRef?.personId ?? '';
  const canRestore = restoreFromVersion > 0 && personId !== '';

  const handleRestore = () => {
    if (!canRestore) return;
    if (!confirm(`Restore this contact to v${restoreFromVersion}? A new audit entry will be written.`)) return;
    restore.mutate({ id: personId, version: restoreFromVersion });
  };

  return (
    <aside className="audit-detail">
      <header className="audit-detail-head">
        <div>
          <div className="ad-meta-line" style={{ color: meta.color }}>
            <Icon name={meta.icon} size={12} />
            {meta.label}
          </div>
          <div className="ad-id">Event {event.id}</div>
        </div>
        <IconButton
          icon="x"
          label="Close detail"
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
        />
      </header>

      <div className="audit-detail-body">
        <div className="ad-section-label">Summary</div>
        <div className="ad-change">
          <div className="ad-change-field">{event.change?.action || 'event'}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
            {event.change?.summary || '—'}
          </div>
        </div>

        <div className="ad-meta">
          <div className="ad-meta-row">
            <span className="label">When</span>
            <span>
              {new Date(event.occurredAt).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </span>
          </div>
          <div className="ad-meta-row">
            <span className="label">Actor</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Avatar initials={actorInitials} size={18} color={colorFromKey(actorId)} />
              <span style={{ fontSize: 12 }}>
                {actorId}
                {event.actor?.type && (
                  <span style={{ marginLeft: 4, fontSize: 10.5, color: 'var(--text-4)' }}>
                    · {event.actor.type}
                  </span>
                )}
              </span>
            </span>
          </div>
          {contactRef && (
            <div className="ad-meta-row">
              <span className="label">Contact</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Avatar initials={initialsOf(contactRef)} size={18} color={colorFromKey(contactRef)} />
                <span style={{ fontSize: 12 }}>{contactRef}</span>
              </span>
            </div>
          )}
          {personId && (
            <div className="ad-meta-row">
              <span className="label">Contact ID</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{personId}</span>
                <IconButton
                  icon="copy"
                  label="Copy contact id"
                  size="sm"
                  iconSize={11}
                  onClick={() => void navigator.clipboard?.writeText(personId)}
                />
              </span>
            </div>
          )}
          {event.docRef?.collection && (
            <div className="ad-meta-row">
              <span className="label">Collection</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>
                {event.docRef.collection}
              </span>
            </div>
          )}
          <div className="ad-meta-row">
            <span className="label">Version</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>
              v{event.change?.versionFrom ?? 0} → v{event.change?.versionTo ?? 0}
            </span>
          </div>
          <div className="ad-meta-row">
            <span className="label">Source</span>
            <span>{event.actor?.type === 'system' ? `API · ${event.actor.ip || ''}` : 'Web app'}</span>
          </div>
          {event.actor?.ip && (
            <div className="ad-meta-row">
              <span className="label">IP</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>{event.actor.ip}</span>
            </div>
          )}
          {event.actor?.userAgent && (
            <div className="ad-meta-row">
              <span className="label">User agent</span>
              <span style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'right' }}>
                {event.actor.userAgent}
              </span>
            </div>
          )}
          <div className="ad-meta-row">
            <span className="label">Event ID</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{event.id}</span>
          </div>
          <div className="ad-meta-row">
            <span className="label">When (UTC)</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              {formatHourMinute(event.occurredAt)} ·{' '}
              {new Date(event.occurredAt).toISOString()}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
          {canRestore && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Icon name="history" size={12} />}
              style={{ flex: 1, justifyContent: 'center' }}
              loading={restore.isPending}
              onClick={handleRestore}
            >
              Restore v{restoreFromVersion}
            </Button>
          )}
          {contactRef && onOpenContact && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Icon name="eye" size={12} />}
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={() => onOpenContact(contactRef)}
            >
              Open contact
            </Button>
          )}
          {personId && onFilterContact && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Icon name="filter" size={12} />}
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={() => onFilterContact(personId)}
            >
              Filter by contact
            </Button>
          )}
        </div>
      </div>
    </aside>
  );
}
